import React from 'react';
import MatchDisplay from './MatchDisplay';
import {
  getPlayoffMatchups,
  getEighthFinalMatchups,
  getQuarterFinalMatchups,
  getSemiFinalMatchups
} from './utils';

const Phase2Tab = ({ player, phaseLocks, realResults }) => {
  // Parse predictions
  let playoffPreds = {};
  let knockoutPreds = {};
  
  try {
    if (player.playoffPredictions && player.playoffPredictions !== '{}') {
      playoffPreds = typeof player.playoffPredictions === 'string' 
        ? JSON.parse(player.playoffPredictions)
        : player.playoffPredictions;
    }
  } catch (e) {
    console.error('Error parsing playoff predictions:', e);
  }

  try {
    if (player.knockoutPredictions && player.knockoutPredictions !== '{}') {
      knockoutPreds = typeof player.knockoutPredictions === 'string' 
        ? JSON.parse(player.knockoutPredictions)
        : player.knockoutPredictions;
    }
  } catch (e) {
    console.error('Error parsing knockout predictions:', e);
  }

  // Hent matchups
  const playoffMatchups = getPlayoffMatchups(realResults);
  const eighthFinalMatchups = getEighthFinalMatchups(realResults);
  const quarterFinalMatchups = getQuarterFinalMatchups(realResults);
  const semiFinalMatchups = getSemiFinalMatchups(realResults);

  // Tjek fase låsning
  const isPlayoffLocked = phaseLocks.phase2Playoff || false;
  const isRound16Locked = phaseLocks.phase2Round16 || false;
  const isQuartersLocked = phaseLocks.phase2Quarters || false;
  const isSemisLocked = phaseLocks.phase2Semis || false;

  // Helper funktion til at finde predicted winner baseret på match.id
  const findPredictedWinner = (match, predictions, index, prefix = '') => {
    const matchId = match.id;
    return predictions[matchId] || 
           predictions[`match-${index + 1}`] ||
           predictions[matchId?.replace(prefix, 'match-')] ||
           predictions[matchId?.replace(/^.*?(\d+).*$/, 'match-$1')];
  };

  // Tjek om der er nogen gæt eller matchups
  const hasAnyGuesses = Object.keys(playoffPreds).length > 0 || 
                       Object.keys(knockoutPreds).length > 0 ||
                       (knockoutPreds.eighthFinals && Object.keys(knockoutPreds.eighthFinals).length > 0) ||
                       (knockoutPreds.quarterFinals && Object.keys(knockoutPreds.quarterFinals).length > 0) ||
                       (knockoutPreds.semiFinals && Object.keys(knockoutPreds.semiFinals).length > 0) ||
                       playoffMatchups.length > 0 ||
                       eighthFinalMatchups.length > 0 ||
                       quarterFinalMatchups.length > 0 ||
                       semiFinalMatchups.length > 0;

  return (
    <div className="guess-section">
      <h3 className="guess-section-title">Knockout</h3>
      {hasAnyGuesses ? (
        <div className="guess-content">
          {/* Playoff Runde */}
          {!isPlayoffLocked && (playoffMatchups.length > 0 || Object.keys(playoffPreds).length > 0) && (
            <div className="round-section">
              <h4 className="round-title">Playoff Runde</h4>
              {playoffMatchups.length > 0 ? (
                <div className="matches-grid">
                  {playoffMatchups.map((match, index) => {
                    const predictedWinner = findPredictedWinner(match, playoffPreds, index, 'playoff_');
                    return (
                      <MatchDisplay
                        key={match.id}
                        match={match}
                        predictedWinner={predictedWinner}
                        realWinner={match.winner}
                      />
                    );
                  })}
                </div>
              ) : (
                Object.keys(playoffPreds).length > 0 && (
                  <div className="knockout-predictions">
                    {Object.entries(playoffPreds).map(([matchId, winner]) => (
                      <div key={matchId} className="prediction-item">
                        <span className="match-id">{matchId}:</span>
                        <span className="winner">{winner}</span>
                      </div>
                    ))}
                  </div>
                )
              )}
            </div>
          )}

          {/* Round of 16 */}
          {!isRound16Locked && (eighthFinalMatchups.length > 0 || (knockoutPreds.eighthFinals && Object.keys(knockoutPreds.eighthFinals).length > 0)) && (
            <div className="round-section">
              <h4 className="round-title">Round of 16</h4>
              {eighthFinalMatchups.length > 0 ? (
                <div className="matches-grid">
                  {eighthFinalMatchups.map((match, index) => {
                    const predictedWinner = knockoutPreds.eighthFinals 
                      ? findPredictedWinner(match, knockoutPreds.eighthFinals, index, 'eighth_')
                      : '';
                    return (
                      <MatchDisplay
                        key={match.id}
                        match={match}
                        predictedWinner={predictedWinner}
                        realWinner={match.winner}
                      />
                    );
                  })}
                </div>
              ) : (
                knockoutPreds.eighthFinals && Object.keys(knockoutPreds.eighthFinals).length > 0 && (
                  <div className="knockout-predictions">
                    {Object.entries(knockoutPreds.eighthFinals).map(([matchId, winner]) => (
                      <div key={matchId} className="prediction-item">
                        <span className="match-id">{matchId}:</span>
                        <span className="winner">{winner}</span>
                      </div>
                    ))}
                  </div>
                )
              )}
            </div>
          )}

          {/* Quarter Finals */}
          {!isQuartersLocked && (quarterFinalMatchups.length > 0 || (knockoutPreds.quarterFinals && Object.keys(knockoutPreds.quarterFinals).length > 0)) && (
            <div className="round-section">
              <h4 className="round-title">Quarter Finals</h4>
              {quarterFinalMatchups.length > 0 ? (
                <div className="matches-grid">
                  {quarterFinalMatchups.map((match, index) => {
                    const predictedWinner = knockoutPreds.quarterFinals
                      ? findPredictedWinner(match, knockoutPreds.quarterFinals, index, 'quarter_')
                      : '';
                    return (
                      <MatchDisplay
                        key={match.id}
                        match={match}
                        predictedWinner={predictedWinner}
                        realWinner={match.winner}
                      />
                    );
                  })}
                </div>
              ) : (
                knockoutPreds.quarterFinals && Object.keys(knockoutPreds.quarterFinals).length > 0 && (
                  <div className="knockout-predictions">
                    {Object.entries(knockoutPreds.quarterFinals).map(([matchId, winner]) => (
                      <div key={matchId} className="prediction-item">
                        <span className="match-id">{matchId}:</span>
                        <span className="winner">{winner}</span>
                      </div>
                    ))}
                  </div>
                )
              )}
            </div>
          )}

          {/* Semi Finals */}
          {!isSemisLocked && (semiFinalMatchups.length > 0 || (knockoutPreds.semiFinals && Object.keys(knockoutPreds.semiFinals).length > 0)) && (
            <div className="round-section">
              <h4 className="round-title">Semi Finals</h4>
              {semiFinalMatchups.length > 0 ? (
                <div className="matches-grid">
                  {semiFinalMatchups.map((match, index) => {
                    const predictedWinner = knockoutPreds.semiFinals
                      ? findPredictedWinner(match, knockoutPreds.semiFinals, index, 'semi_')
                      : '';
                    return (
                      <MatchDisplay
                        key={match.id}
                        match={match}
                        predictedWinner={predictedWinner}
                        realWinner={match.winner}
                      />
                    );
                  })}
                </div>
              ) : (
                knockoutPreds.semiFinals && Object.keys(knockoutPreds.semiFinals).length > 0 && (
                  <div className="knockout-predictions">
                    {Object.entries(knockoutPreds.semiFinals).map(([matchId, winner]) => (
                      <div key={matchId} className="prediction-item">
                        <span className="match-id">{matchId}:</span>
                        <span className="winner">{winner}</span>
                      </div>
                    ))}
                  </div>
                )
              )}
            </div>
          )}

          <p className="guess-points">
            Point: {player.phase2Points || 0}
          </p>
        </div>
      ) : (
        <p className="no-guess">Ikke gættet endnu</p>
      )}
    </div>
  );
};

export default Phase2Tab;

